<template>
  <div>
    <b-card
      class="mb-3"
      aria-label="Filtres de recherche des bons de transfert logistiques"
    >
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Filtres logistiques
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row class="mt-1">
          <b-col md="3">
            <b-form-group
              label="Référence du bon de transfert"
              label-for="reference"
              aria-label="Filtrer par référence du bon de transfert"
              description="Saisissez la référence pour filtrer les bons."
            >
              <b-form-input
                v-model="filters.reference"
                placeholder="Rechercher par référence de transfert"
                aria-label="Entrée de la référence du bon de transfert"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Dépôt source"
              label-for="fromRepository"
              aria-label="Filtrer par dépôt source"
              description="Sélectionnez le dépôt d'origine."
            >
              <v-select
                v-model="filters.fromRepository"
                :clearable="true"
                placeholder="Sélectionner un dépôt source"
                label="name"
                :options="repositories"
                aria-label="Liste des dépôts source disponibles"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Dépôt destination"
              label-for="toRepository"
              aria-label="Filtrer par dépôt de destination"
              description="Sélectionnez le dépôt de destination."
            >
              <v-select
                v-model="filters.toRepository"
                :clearable="true"
                placeholder="Sélectionner un dépôt de destination"
                label="name"
                :options="repositories"
                aria-label="Liste des dépôts de destination disponibles"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Livreur"
              label-for="deliveryMan"
              aria-label="Filtrer par livreur"
              description="Sélectionnez le livreur responsable."
            >
              <v-select
                v-model="filters.deliveryMan"
                :clearable="true"
                placeholder="Sélectionner un livreur"
                label="username"
                :options="deliveryMen"
                aria-label="Liste des livreurs disponibles"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Statut de transfert"
              label-for="status"
              aria-label="Filtrer par statut de transfert"
              description="Filtrez selon le statut du transfert."
            >
              <b-form-select
                v-model="filters.isTransfer"
                :options="transferStatusOptions"
                aria-label="Sélection du statut de transfert"
                @change="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date début"
              label-for="dateFrom"
              aria-label="Filtrer par date de début"
              description="Sélectionnez la date de début du transfert."
            >
              <b-form-datepicker
                v-model="filters.dateFrom"
                v-bind="datePickerConfig"
                placeholder="Date de début"
                reset-button
                close-button
                aria-label="Sélection de la date de début"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date fin"
              label-for="dateTo"
              aria-label="Filtrer par date de fin"
              description="Sélectionnez la date de fin du transfert."
            >
              <b-form-datepicker
                v-model="filters.dateTo"
                v-bind="datePickerConfig"
                placeholder="Date de fin"
                reset-button
                close-button
                aria-label="Sélection de la date de fin"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Bouton de réinitialisation des filtres -->
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              aria-label="Réinitialiser les filtres de recherche des bons de transfert"
              @click="clearFilters"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Réinitialiser les filtres
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Action Buttons -->
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-between align-items-center">
        <div
          v-if="user.role === 'manager'"
          class="d-flex align-items-center"
        >
          <label class="mr-3 h5 mb-0">Vue:</label>
          <b-form-radio-group
            v-model="status"
            class="d-flex"
            @input="handleViewChange"
          >
            <b-form-radio
              :value="0"
              class="mr-3"
            >
              Envoyés
            </b-form-radio>
            <b-form-radio :value="1">
              Reçus
            </b-form-radio>
          </b-form-radio-group>
        </div>

        <b-button
          :to="{ name: 'bon-transfert-add' }"
          variant="gradient-primary"
          class="ml-auto"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Nouveau transfert</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement des bons de transfert...
      </p>
    </div>

    <!-- Empty State - No Data at All -->
    <b-card
      v-else-if="!isLoading && transferVouchers.length === 0 && !hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="TruckIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun bon de transfert
        </h4>
        <p class="text-muted mb-3">
          Il n'y a pas encore de bons de transfert dans le système.
        </p>
        <b-button
          :to="{ name: 'bon-transfert-add' }"
          variant="primary"
          aria-label="Créer le premier bon de transfert logistique"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Créer le premier bon de transfert
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Empty State - No Results for Filters -->
    <b-card
      v-else-if="!isLoading && transferVouchers.length === 0 && hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="SearchIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun résultat trouvé
        </h4>
        <p class="text-muted mb-3">
          Aucun bon de transfert ne correspond aux critères de recherche sélectionnés.
        </p>
        <b-button
          variant="outline-primary"
          aria-label="Réinitialiser les filtres de recherche des bons de transfert"
          @click="clearFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Réinitialiser les filtres
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Data Table -->
    <vue-good-table
      v-else
      :columns="tableColumns"
      :rows="transferVouchers"
      :search-options="{ enabled: true, externalQuery: searchTerm }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div v-if="props.column.field === 'id'">
          <qrcode-vue
            :value="props.row.id.toString()"
            :size="size"
            level="H"
            :aria-label="`QR code de la référence du bon de transfert ${props.row.id}`"
          />
          <p class="mb-0 mt-1">
            {{ props.row.id }}
          </p>
        </div>

        <span v-else-if="props.column.field === 'is_transfer'">
          <b-badge
            :variant="props.row.is_transfer ? 'success' : 'warning'"
            :aria-label="props.row.is_transfer ? 'Transfert validé' : 'Transfert en attente'"
          >
            {{ props.row.is_transfer_display }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'total'">
          <strong aria-label="Montant total du transfert">{{ formatPrice(props.row.total) }} TND</strong>
        </span>

        <span v-else-if="props.column.field === 'orders_count'">
          <b-badge
            variant="info"
            pill
            aria-label="Nombre de colis transférés"
          >{{ props.row.orders_count }}</b-badge>
        </span>

        <span v-else-if="props.column.field === 'action'">
          <div class="d-flex">
            <router-link
              class="text-info mr-1"
              :to="{ name: 'bon-transfert-show', params: { id: props.row.id } }"
              :aria-label="`Voir les détails du bon de transfert ${props.row.id}`"
            >
              <feather-icon
                :id="`transfer-row-${props.row.id}-show-icon`"
                icon="EyeIcon"
                class="cursor-pointer"
                size="16"
              />
            </router-link>
            <b-tooltip
              title="Voir les détails"
              :target="`transfer-row-${props.row.id}-show-icon`"
            />

          </div>
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">Affichage 1 à</span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value) => props.perPageChanged({ currentPerPage: value })"
            />
            <span class="text-nowrap">de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormSelect, BButton, BFormInput,
  BSpinner, BTooltip, BCol, BRow, BCard, BCardHeader, BCardBody, BBadge,
  BFormRadioGroup, BFormRadio, BFormDatepicker,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    BTooltip,
    QrcodeVue,
    vSelect,
    BCol,
    BRow,
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BFormInput,
    BFormRadioGroup,
    BFormRadio,
    BFormDatepicker,
  },
  data() {
    return {
      size: 40,
      isLoading: false,
      pageLength: 100,
      currentPage: 1,
      count: 0,
      status: 0,
      columns: [
        {
          label: 'Référence',
          field: 'id',
        },
        {
          label: 'Dépôt source',
          field: 'from_repository',
        },
        {
          label: 'Dépôt destination',
          field: 'to_repository',
        },
        {
          label: 'Livreur',
          field: 'delivery_man',
        },
        {
          label: 'Voiture',
          field: 'car',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Total (TND)',
          field: 'total',
        },
        {
          label: 'Statut',
          field: 'is_transfer',
        },
        {
          label: 'Date de création',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Actions',
          field: 'action',
        },
      ],
      transferVouchers: [],
      searchTerm: '',
      repositories: [],
      deliveryMen: [],
      filters: {
        reference: '',
        fromRepository: null,
        toRepository: null,
        deliveryMan: null,
        isTransfer: '',
        dateFrom: '',
        dateTo: '',
      },
      transferStatusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: true, text: 'Transféré' },
        { value: false, text: 'En cours' },
      ],
      user: {},
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1,
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
      },
    }
  },
  computed: {
    tableColumns() {
      if (this.user.role === 'admin') {
        return this.columns
      }
      return this.columns.filter(column => !['from_repository', 'to_repository'].includes(column.field))
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== null && filter !== '' && filter !== undefined)
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getTransferVouchers(1)
    this.getDeliveryMen()
    if (this.user.role === 'admin') {
      this.getRepositories()
    }
  },
  methods: {
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des dépôts')
      }
    },
    async getDeliveryMen() {
      try {
        const { data } = await axios.get('api/auth/users/drivers/')
        this.deliveryMen = data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des livreurs')
      }
    },
    handleViewChange() {
      this.currentPage = 1
      this.getTransferVouchers(1)
    },
    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatPrice(price) {
      return price ? price.toFixed(3) : '0.000'
    },
    applyFilters() {
      this.currentPage = 1
      this.getTransferVouchers(1)
    },
    clearFilters() {
      this.filters = {
        reference: '',
        fromRepository: null,
        toRepository: null,
        deliveryMan: null,
        isTransfer: '',
        dateFrom: '',
        dateTo: '',
      }
      this.currentPage = 1
      this.getTransferVouchers(1)
    },
    buildFilterParams() {
      const params = { p: this.currentPage }

      if (this.filters.reference) {
        params.id = this.filters.reference
      }
      if (this.filters.fromRepository) {
        params.from_repository = this.filters.fromRepository.id
      }
      if (this.filters.toRepository) {
        params.to_repository = this.filters.toRepository.id
      }
      if (this.filters.deliveryMan) {
        params.delivery_man = this.filters.deliveryMan.id
      }
      if (this.filters.isTransfer !== '') {
        params.is_transfer = this.filters.isTransfer
      }
      if (this.filters.dateFrom) {
        params.created_at__gte = this.filters.dateFrom
      }
      if (this.filters.dateTo) {
        params.created_at__lte = this.filters.dateTo
      }

      return params
    },
    pageNext(currentPage) {
      this.getTransferVouchers(currentPage)
    },
    async getTransferVouchers(currentPage) {
      this.isLoading = true
      try {
        let url = 'api/transfer-vouchers/list/'
        let params = this.buildFilterParams()
        params.p = currentPage

        if (this.user.role === 'manager') {
          if (this.status === 0) {
            params.from_repository = this.user.repository
          } else {
            url = 'api/transfer-vouchers/'
            params = { to_repository: this.user.repository }
          }
        }

        const { data } = await axios.get(url, { params })

        if (this.user.role === 'manager' && this.status === 1) {
          this.transferVouchers = data
          this.count = data.length
        } else {
          this.transferVouchers = data.results
          this.count = data.count
        }
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des données')
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    clearDateFilter(field) {
      this.filters[field] = null
      this.applyFilters()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
